import { create } from 'zustand';
import ServiceHorarios from '@/services/ServiceHorarios';
import { notify } from '@/stores/useToast';
import {
  buildRequestBody,
  buildDashboardBody,
  buildStepVM,
  buildStatusVM,
  buildClassificationVM,
  buildRiskVM,
  STEP_LABEL,
  STATUS_LABEL,
  CLASSIFICATION_LABEL,
  RISK_LABEL,
  FILTROS_OPCOES_VAZIO,
  type Barra,
  type HorarioTableFiltros,
  type JornadaFiltrosOpcoes,
} from '@/lib/jornada';

export interface Ocorrencia {
  id: number;
  occurrence: string;
}

export interface LinhaPorOcorrencia {
  [k: string]: unknown;
  _key: string;
  cnpj: string;
  empresa: string;
  risk: string; // enum cru (p/ tag de risco)
  riskLabel: string;
  occurrenceId: number | null;
  occurrence: string;
  diagnosis: string;
  occurrenceCount: number;
}

export interface LinhaDetalhe {
  [k: string]: unknown;
  _key: string;
  nome: string;
  matricula: string;
  cpf: string;
}

export interface HistoricoMovimentacao {
  [k: string]: unknown;
  changedFieldDescription?: string | null;
  changedField?: string | null;
  fromValue?: string | null;
  toValue?: string | null;
  updatedBy?: string | null;
  updatedByName?: string | null;
  updatedAt?: string | null;
}

export interface LinhaHorario {
  [k: string]: unknown;
  _key: string;
  idTrabalhador: number | null;
  empresa: string;
  cnpj: string;
  cpf: string;
  matricula: string;
  nome: string;
  nomeSocial: string;
  ocupacao: string;
  cargo: string;
  codigoCategoria: string;
  categoria: string;
  regimeTrabalhista: string;
  regimePrevidenciario: string;
  etapa: string;
  status: string;
  classificacao: string;
  risco: string; // enum cru (p/ tag de risco)
  analiseId: number | null; // id da análise (p/ dialog de movimentações); null = sem análise
  historicoDisponivel: boolean;
  regimeJornada: string;
  tipoJornada: string;
  cargaHorariaSemanal: number | null;
  tempoParcial: string;
  horarioNoturno: string;
  descricaoJornada: string;
  dsr: string;
  identificacaoEvento: string;
  envioEvento: string;
  versaoEsocial: string;
  declaracao: string;
  reciboRetificacao: string;
  ocorrenciaRec: string;
  diagnosticoRec: string;
  recRegimeJornada: string;
  recTipoJornada: string;
  recCargaHorariaSemanal: number | null;
  recTempoParcial: string;
  recHorarioNoturno: string;
  recDescricaoJornada: string;
}

const FILTROS_VAZIO: HorarioTableFiltros = {
  cnpjList: [],
  step: [],
  status: [],
  classification: [],
  risk: [],
  occupationCode: [],
  cargo: [],
  categoria: [],
  tipoRegistro: [],
  regimePrevidenciario: [],
  tipoJornada: [],
  cargaHorariaSemanal: [],
  horarioNoturno: null,
  tempoParcial: [],
  versao: [],
  declaracao: [],
  envioInicio: null,
  envioFim: null,
};

// CON-5188: coluna→alias aceito pelo backend (trabalhador-jornada?sort=<alias>,<asc|desc>).
// Tabela de trabalhadores (Visão geral + Risco > Todos, mesmo endpoint/allow-list). Só as
// colunas com alias entram como sortable. Quase tudo é identidade; exceções: empresa→nomeEmpresa
// e risco→grauRisco.
export const SORT_ALIASES: Record<string, string> = {
  empresa: 'nomeEmpresa',
  cpf: 'cpf',
  matricula: 'matricula',
  nome: 'nome',
  ocupacao: 'ocupacao',
  cargo: 'cargo',
  codigoCategoria: 'codigoCategoria',
  categoria: 'categoria',
  regimeTrabalhista: 'regimeTrabalhista',
  regimePrevidenciario: 'regimePrevidenciario',
  etapa: 'etapa',
  status: 'status',
  classificacao: 'classificacao',
  risco: 'grauRisco',
  regimeJornada: 'regimeJornada',
  tipoJornada: 'tipoJornada',
  cargaHorariaSemanal: 'cargaHorariaSemanal',
  tempoParcial: 'tempoParcial',
  horarioNoturno: 'horarioNoturno',
  descricaoJornada: 'descricaoJornada',
  dsr: 'dsr',
  identificacaoEvento: 'identificacaoEvento',
  envioEvento: 'envioEvento',
  versaoEsocial: 'versaoEsocial',
  declaracao: 'declaracao',
  reciboRetificacao: 'reciboRetificacao',
  ocorrenciaRec: 'ocorrenciaRec',
  diagnosticoRec: 'diagnosticoRec',
  recRegimeJornada: 'recRegimeJornada',
  recTipoJornada: 'recTipoJornada',
  recCargaHorariaSemanal: 'recCargaHorariaSemanal',
  recTempoParcial: 'recTempoParcial',
  recHorarioNoturno: 'recHorarioNoturno',
  recDescricaoJornada: 'recDescricaoJornada',
};

// Aba Risco > Por ocorrência: allow-list própria do endpoint /com-risco/por-ocorrencia.
export const PO_SORT_ALIASES: Record<string, string> = {
  empresa: 'socialName',
  risco: 'risk',
  occurrence: 'occurrence',
  diagnosis: 'diagnosis',
  occurrenceCount: 'occurrenceCount',
};

// monta o `sort` (ou undefined) a partir do estado e do mapa de aliases
const buildSort = (map: Record<string, string>, by: string, order: 'asc' | 'desc') => {
  const alias = map[by];
  return alias ? `${alias},${order}` : undefined;
};

type Any = any;

const normalizar = (h: Any, i: number): LinhaHorario => ({
  _key: `${h.idTrabalhador ?? 'row'}-${i}`,
  idTrabalhador: h.idTrabalhador ?? null,
  empresa: h.nomeEmpresa ?? '',
  cnpj: h.cnpj ?? '',
  cpf: h.cpf ?? '',
  matricula: h.matricula ?? '',
  nome: h.nomeSocial ? `${h.nome ?? ''} / ${h.nomeSocial}` : (h.nome ?? ''),
  nomeSocial: h.nomeSocial ?? '',
  ocupacao: h.ocupacao ?? '',
  cargo: h.cargo ?? '',
  codigoCategoria: h.codigoCategoria ?? '',
  categoria: h.categoria ?? '',
  regimeTrabalhista: h.regimeTrabalhista ?? '',
  regimePrevidenciario: h.regimePrevidenciario ?? '',
  etapa: STEP_LABEL[h.etapa as string] ?? h.etapa ?? '',
  status: STATUS_LABEL[h.status as string] ?? h.status ?? '',
  classificacao: CLASSIFICATION_LABEL[h.classificacao as string] ?? h.classificacao ?? '',
  risco: (h.risco as string) ?? '',
  analiseId: h.analiseId ?? null,
  historicoDisponivel: h.possuiHistorico === true,
  regimeJornada: (h.regimeJornada as string) ?? '',
  tipoJornada: (h.tipoJornada as string) ?? '',
  cargaHorariaSemanal: (h.cargaHorariaSemanal as number) ?? null,
  tempoParcial: (h.tempoParcial as string) ?? '',
  horarioNoturno: (h.horarioNoturno as string) ?? '',
  descricaoJornada: (h.descricaoJornada as string) ?? '',
  dsr: (h.dsr as string) ?? '',
  identificacaoEvento: (h.identificacaoEvento as string) ?? '',
  envioEvento: (h.envioEvento as string) ?? '',
  versaoEsocial: (h.versaoEsocial as string) ?? '',
  declaracao: (h.declaracao as string) ?? '',
  reciboRetificacao: (h.reciboRetificacao as string) ?? '',
  ocorrenciaRec: (h.ocorrenciaRec as string) ?? '',
  diagnosticoRec: (h.diagnosticoRec as string) ?? '',
  recRegimeJornada: (h.recRegimeJornada as string) ?? '',
  recTipoJornada: (h.recTipoJornada as string) ?? '',
  recCargaHorariaSemanal: (h.recCargaHorariaSemanal as number) ?? null,
  recTempoParcial: (h.recTempoParcial as string) ?? '',
  recHorarioNoturno: (h.recHorarioNoturno as string) ?? '',
  recDescricaoJornada: (h.recDescricaoJornada as string) ?? '',
});

const normalizarPO = (p: Any, i: number): LinhaPorOcorrencia => ({
  _key: `${p.cnpj ?? 'row'}-${p.occurrenceId ?? i}-${i}`,
  cnpj: p.cnpj ?? '',
  empresa: p.socialName ?? '',
  risk: p.risk ?? '',
  riskLabel: RISK_LABEL[p.risk as string] ?? p.risk ?? '',
  occurrenceId: p.occurrenceId ?? null,
  occurrence: p.occurrence ?? '',
  diagnosis: p.diagnosis ?? '',
  occurrenceCount: p.occurrenceCount ?? 0,
});

const normalizarDetalhe = (d: Any, i: number): LinhaDetalhe => ({
  _key: `${d.cpf ?? 'row'}-${i}`,
  nome: d.trabalhadorNome ?? '',
  matricula: d.matricula ?? '',
  cpf: d.cpf ?? '',
});

interface State {
  empregador: string;
  filtros: HorarioTableFiltros;
  opcoesFiltro: JornadaFiltrosOpcoes;
  busca: string;
  stepVM: Barra[];
  statusVM: Barra[];
  classificationVM: Barra[];
  riskVM: Barra[];
  riskTotal: number;
  lista: LinhaHorario[];
  total: number;
  page: number;
  size: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  carregandoGraficos: boolean;
  carregandoLista: boolean;
  exportando: boolean;

  // aba Risco > Todos
  rtLista: LinhaHorario[];
  rtTotal: number;
  rtPage: number;
  rtSize: number;
  rtSortBy: string;
  rtSortOrder: 'asc' | 'desc';
  rtBusca: string;
  rtRisk: string[];
  rtOccurrence: number[];
  rtCarregando: boolean;
  rtExportando: boolean;
  // aba Risco > Por ocorrência
  poLista: LinhaPorOcorrencia[];
  poTotal: number;
  poPage: number;
  poSize: number;
  poSortBy: string;
  poSortOrder: 'asc' | 'desc';
  poBusca: string;
  poRiskLevel: string[];
  poCarregando: boolean;
  poExportando: boolean;
  // filtro de ocorrências (compartilhado pelas abas de risco)
  ocorrencias: Ocorrencia[];

  carregarVisaoGeral: () => Promise<void>;
  carregarRisco: () => Promise<void>;
  carregarHistorico: () => Promise<void>;
  carregarFiltros: () => Promise<void>;
  carregarRiscoTodos: () => Promise<void>;
  setRtBusca: (v: string) => void;
  buscarRt: () => Promise<void>;
  setRtFiltros: (f: { risk: string[]; occurrence: number[] }) => Promise<void>;
  setRtPage: (page: number) => Promise<void>;
  setRtSize: (size: number) => Promise<void>;
  setRtSort: (by: string, order: 'asc' | 'desc') => Promise<void>;
  carregarPorOcorrencia: () => Promise<void>;
  setPoBusca: (v: string) => void;
  buscarPo: () => Promise<void>;
  setPoRiskLevel: (risk: string[]) => Promise<void>;
  setPoPage: (page: number) => Promise<void>;
  setPoSize: (size: number) => Promise<void>;
  setPoSort: (by: string, order: 'asc' | 'desc') => Promise<void>;
  carregarOcorrencias: () => Promise<void>;
  fetchDetalhe: (cnpj: string, recommendationId: number) => Promise<LinhaDetalhe[]>;
  fetchMovimentacoes: (analiseId: number) => Promise<HistoricoMovimentacao[]>;
  exportarMovimentacoes: (analiseId: number) => Promise<void>;
  aplicarEmpregador: (cnpj: string) => Promise<void>;
  setFiltros: (f: HorarioTableFiltros) => Promise<void>;
  limparFiltros: () => Promise<void>;
  setBusca: (v: string) => void;
  buscar: () => Promise<void>;
  setPage: (page: number) => Promise<void>;
  setSize: (size: number) => Promise<void>;
  setSort: (by: string, order: 'asc' | 'desc') => Promise<void>;
  exportar: () => Promise<void>;
  exportarRiscoTodos: () => Promise<void>;
  exportarPorOcorrencia: () => Promise<void>;
  poDetalheExportando: boolean;
  exportarPorOcorrenciaDetalhe: (cnpj: string, recommendationId: number) => Promise<void>;
  movExportando: boolean;
}

// tabela: filtro geral (empregador) + filtros da tabela + busca (filterTerm)
function _body(get: () => State) {
  const s = get();
  return buildRequestBody({
    cnpjList: s.empregador ? [s.empregador] : [],
    busca: s.busca,
    filtros: s.filtros,
  });
}

// gráficos: só o filtro geral (empregador)
function _dashBody(get: () => State) {
  const s = get();
  return buildDashboardBody(s.empregador ? [s.empregador] : []);
}

// aba Risco > Todos: filtro geral + busca + risco/ocorrência selecionados
function _rtBody(get: () => State) {
  const s = get();
  return {
    cnpjList: s.empregador ? [s.empregador] : [],
    filterTerm: s.rtBusca.trim() || undefined,
    risk: s.rtRisk,
    occurrence: s.rtOccurrence,
  };
}

// aba Risco > Por ocorrência: filtro geral + busca + grau de risco selecionado
function _poBody(get: () => State) {
  const s = get();
  return {
    cnpjList: s.empregador ? [s.empregador] : [],
    search: s.poBusca.trim() || undefined,
    riskLevel: s.poRiskLevel,
  };
}

export const useHorariosStore = create<State>((set, get) => ({
  empregador: '',
  filtros: { ...FILTROS_VAZIO },
  opcoesFiltro: { ...FILTROS_OPCOES_VAZIO },
  busca: '',
  stepVM: buildStepVM(undefined),
  statusVM: buildStatusVM(undefined),
  classificationVM: buildClassificationVM(undefined),
  riskVM: buildRiskVM(undefined),
  riskTotal: 0,
  lista: [],
  total: 0,
  page: 0,
  size: 50,
  sortBy: '',
  sortOrder: 'asc',
  carregandoGraficos: false,
  carregandoLista: false,
  exportando: false,
  rtLista: [],
  rtTotal: 0,
  rtPage: 0,
  rtSize: 50,
  rtSortBy: '',
  rtSortOrder: 'asc',
  rtBusca: '',
  rtRisk: [],
  rtOccurrence: [],
  rtCarregando: false,
  rtExportando: false,
  movExportando: false,
  poLista: [],
  poTotal: 0,
  poPage: 0,
  poSize: 50,
  poSortBy: '',
  poSortOrder: 'asc',
  poBusca: '',
  poRiskLevel: [],
  poCarregando: false,
  poExportando: false,
  poDetalheExportando: false,
  ocorrencias: [],

  async carregarVisaoGeral() {
    set({ carregandoGraficos: true });
    try {
      const { data } = await ServiceHorarios.dashboard(_dashBody(get));
      set({
        stepVM: buildStepVM(data),
        statusVM: buildStatusVM(data),
        classificationVM: buildClassificationVM(data),
      });
    } catch {
      set({
        stepVM: buildStepVM(undefined),
        statusVM: buildStatusVM(undefined),
        classificationVM: buildClassificationVM(undefined),
      });
      notify.error('Erro ao carregar os gráficos de análise de horários e jornada.');
    } finally {
      set({ carregandoGraficos: false });
    }
    await get().carregarHistorico();
  },

  async carregarRisco() {
    set({ carregandoGraficos: true });
    try {
      const { data } = await ServiceHorarios.grafico(_dashBody(get));
      set({ riskVM: buildRiskVM(data), riskTotal: data?.totalItems ?? 0 });
    } catch {
      set({ riskVM: buildRiskVM(undefined), riskTotal: 0 });
      notify.error('Erro ao carregar o gráfico de exposição ao risco.');
    } finally {
      set({ carregandoGraficos: false });
    }
    // A aba Risco não tem tabela de trabalhadores (com-risco = Fase 2), então não
    // recarrega o histórico aqui — evita request desperdiçado e toast espúrio.
  },

  async carregarHistorico() {
    const { page, size, sortBy, sortOrder } = get();
    set({ carregandoLista: true });
    try {
      const { data } = await ServiceHorarios.list(_body(get), {
        page,
        size,
        sort: buildSort(SORT_ALIASES, sortBy, sortOrder),
      });
      set({
        lista: (data.content ?? []).map(normalizar),
        total: data.totalElements ?? 0,
      });
    } catch {
      set({ lista: [], total: 0 });
      notify.error('Erro ao carregar os trabalhadores.');
    } finally {
      set({ carregandoLista: false });
    }
  },

  async carregarFiltros() {
    try {
      const { data } = await ServiceHorarios.filters(null);
      set({ opcoesFiltro: { ...FILTROS_OPCOES_VAZIO, ...data } });
    } catch {
      set({ opcoesFiltro: { ...FILTROS_OPCOES_VAZIO } });
      notify.error('Erro ao carregar as opções de filtro.');
    }
  },

  async aplicarEmpregador(cnpj) {
    set({ empregador: cnpj, page: 0 });
  },

  async setFiltros(f) {
    set({ filtros: f, page: 0 });
    await get().carregarHistorico();
  },

  async limparFiltros() {
    set({ filtros: { ...FILTROS_VAZIO }, page: 0 });
    await get().carregarHistorico();
  },

  setBusca(v) {
    set({ busca: v });
  },

  async buscar() {
    set({ page: 0 });
    await get().carregarHistorico();
  },

  async setPage(page) {
    set({ page });
    await get().carregarHistorico();
  },

  async setSize(size) {
    set({ size, page: 0 });
    await get().carregarHistorico();
  },

  async setSort(by, order) {
    set({ sortBy: by, sortOrder: order, page: 0 });
    await get().carregarHistorico();
  },

  // ── Aba Risco > Todos ──────────────────────────────────────────────
  async carregarRiscoTodos() {
    const s = get();
    const body = _rtBody(get);
    set({ rtCarregando: true });
    try {
      const { data } = await ServiceHorarios.comRiscoTodos(body, {
        page: s.rtPage,
        size: s.rtSize,
        sort: buildSort(SORT_ALIASES, s.rtSortBy, s.rtSortOrder),
      });
      set({ rtLista: (data.content ?? []).map(normalizar), rtTotal: data.totalElements ?? 0 });
    } catch {
      set({ rtLista: [], rtTotal: 0 });
      notify.error('Erro ao carregar os trabalhadores com risco.');
    } finally {
      set({ rtCarregando: false });
    }
  },

  setRtBusca(v) {
    set({ rtBusca: v });
  },

  async buscarRt() {
    set({ rtPage: 0 });
    await get().carregarRiscoTodos();
  },

  async setRtFiltros(f) {
    set({ rtRisk: f.risk, rtOccurrence: f.occurrence, rtPage: 0 });
    await get().carregarRiscoTodos();
  },

  async setRtPage(page) {
    set({ rtPage: page });
    await get().carregarRiscoTodos();
  },

  async setRtSize(size) {
    set({ rtSize: size, rtPage: 0 });
    await get().carregarRiscoTodos();
  },

  async setRtSort(by, order) {
    set({ rtSortBy: by, rtSortOrder: order, rtPage: 0 });
    await get().carregarRiscoTodos();
  },

  // ── Aba Risco > Por ocorrência ─────────────────────────────────────
  async carregarPorOcorrencia() {
    const s = get();
    const body = _poBody(get);
    set({ poCarregando: true });
    try {
      const { data } = await ServiceHorarios.porOcorrencia(body, {
        page: s.poPage,
        size: s.poSize,
        sort: buildSort(PO_SORT_ALIASES, s.poSortBy, s.poSortOrder),
      });
      set({ poLista: (data.content ?? []).map(normalizarPO), poTotal: data.totalElements ?? 0 });
    } catch {
      set({ poLista: [], poTotal: 0 });
      notify.error('Erro ao carregar as ocorrências.');
    } finally {
      set({ poCarregando: false });
    }
  },

  setPoBusca(v) {
    set({ poBusca: v });
  },

  async buscarPo() {
    set({ poPage: 0 });
    await get().carregarPorOcorrencia();
  },

  async setPoRiskLevel(risk) {
    set({ poRiskLevel: risk, poPage: 0 });
    await get().carregarPorOcorrencia();
  },

  async setPoPage(page) {
    set({ poPage: page });
    await get().carregarPorOcorrencia();
  },

  async setPoSize(size) {
    set({ poSize: size, poPage: 0 });
    await get().carregarPorOcorrencia();
  },

  async setPoSort(by, order) {
    set({ poSortBy: by, poSortOrder: order, poPage: 0 });
    await get().carregarPorOcorrencia();
  },

  async carregarOcorrencias() {
    try {
      const { data } = await ServiceHorarios.comRiscoFiltros();
      set({ ocorrencias: data.occurrences ?? [] });
    } catch {
      set({ ocorrencias: [] });
    }
  },

  async fetchDetalhe(cnpj, recommendationId) {
    try {
      const { data } = await ServiceHorarios.porOcorrenciaDetalhe(cnpj, recommendationId, {
        page: 0,
        size: 500,
      });
      return (data.content ?? []).map(normalizarDetalhe);
    } catch {
      notify.error('Erro ao carregar os trabalhadores da ocorrência.');
      return [];
    }
  },

  async exportar() {
    set({ exportando: true });
    try {
      await ServiceHorarios.exportList(_body(get));
      notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
    } catch {
      notify.error('Erro ao exportar os horários e jornadas.');
    } finally {
      set({ exportando: false });
    }
  },

  async exportarRiscoTodos() {
    set({ rtExportando: true });
    try {
      await ServiceHorarios.exportComRiscoTodos(_rtBody(get));
      notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
    } catch {
      notify.error('Erro ao exportar os trabalhadores com risco.');
    } finally {
      set({ rtExportando: false });
    }
  },

  async exportarPorOcorrencia() {
    set({ poExportando: true });
    try {
      await ServiceHorarios.exportPorOcorrencia(_poBody(get));
      notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
    } catch {
      notify.error('Erro ao exportar as ocorrências.');
    } finally {
      set({ poExportando: false });
    }
  },

  async exportarPorOcorrenciaDetalhe(cnpj, recommendationId) {
    set({ poDetalheExportando: true });
    try {
      await ServiceHorarios.exportPorOcorrenciaDetalhe(cnpj, recommendationId);
      notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
    } catch {
      notify.error('Erro ao exportar os trabalhadores da ocorrência.');
    } finally {
      set({ poDetalheExportando: false });
    }
  },

  // ── Dialog Movimentações (histórico da análise) ────────────────────
  async fetchMovimentacoes(analiseId) {
    try {
      const { data } = await ServiceHorarios.movimentacoes(analiseId, {}, { page: 0, size: 100 });
      return data.content ?? [];
    } catch {
      notify.error('Erro ao carregar as movimentações.');
      return [];
    }
  },

  async exportarMovimentacoes(analiseId) {
    set({ movExportando: true });
    try {
      await ServiceHorarios.exportMovimentacoes(analiseId, {});
      notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
    } catch {
      notify.error('Erro ao exportar as movimentações.');
    } finally {
      set({ movExportando: false });
    }
  },
}));
